import React, { useCallback, useMemo } from "react";
import useAuth from "../../../../Hooks/useAuth";
import usePermission from "../../../../Hooks/usePermission";
import { useGetCustomersByDealerFirmIdQuery } from "../../../../store/storageRoles/firmApi";
import { useUpdateFirmDealerRelationMutation } from "../../../../store";
import SubFirmsTab from "./SubFirmsTab";

/**
 * Bayi firma > Müşteriler sekmesi (merkez paneli, #376) — eski "Alt Bayiler"
 * ve "Müşteriler" sekmeleri tek listede. Alt bayi satırları SubFirmsTab'ın
 * kendi verisinden (`firms.belowFirms`), müşteri satırları bayinin müşteri
 * ucundan (`firms.customers`) gelir; her satır kendi iznine bağlıdır.
 *
 * Müşteri satırında: Kota Ver yalnız bayinin havuzundan, Kota Al canlı okur
 * (#303); Bağı Kopar bayi bağlantısını kaldırır (#202, `firms.editDealerRelation`).
 */
export default function CustomersTab({ firm, onSelectedFirm }) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const canSeeCustomers = hasPermission("firms.customers");

  const { data, isLoading, isFetching, refetch } = useGetCustomersByDealerFirmIdQuery(
    { token: auth?.accessToken, dealerFirmId: firm?._id },
    { skip: !canSeeCustomers || !auth?.accessToken || !firm?._id || firm?.firmType !== "dealer" }
  );
  const [updateDealerRelation, { isLoading: unlinkLoading }] = useUpdateFirmDealerRelationMutation();

  const unlink = useCallback(
    (customer) => {
      const dealerId = String(firm._id);
      const remaining = (customer.dealerFirm || [])
        .map((d) => String(d?._id || d))
        .filter((id) => id !== dealerId);
      return updateDealerRelation({
        id: customer._id,
        dealerFirmIds: remaining,
        token: auth.accessToken,
      }).unwrap();
    },
    [firm?._id, auth?.accessToken, updateDealerRelation]
  );

  const customersSource = useMemo(
    () => ({
      customers: canSeeCustomers ? data?.data?.customers || [] : [],
      loading: canSeeCustomers && isLoading,
      fetching: isFetching,
      refetch,
      canSendQuota: hasPermission("firms.sendSubFirmQuota"),
      canUnlink: hasPermission("firms.editDealerRelation"),
      unlink,
      unlinkLoading,
      limitToPool: true,
      requireLiveSource: true,
    }),
    [canSeeCustomers, data, isLoading, isFetching, refetch, hasPermission, unlink, unlinkLoading]
  );

  return (
    <SubFirmsTab
      firm={firm}
      onSelectedFirm={onSelectedFirm}
      title="Müşteriler"
      customersSource={customersSource}
      includeSubDealers={hasPermission("firms.belowFirms")}
    />
  );
}
